import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useCallback, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ShuttleRackParams, Templates } from '@thive/fddm-types';

import { Accordion } from '@common/components/Accordion';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { NumberInput } from '@/modules/common/components/inputs';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { LoadingServiceFactory } from '@/modules/app/service/LoadingServiceFactory';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { getCommonNumber, getCommonOption } from '@/modules/common/helpers/commonValue';

import { TpsServiceFactory } from '../service/TpsServiceFactory';
import { SHUTTLE_RACK } from '../constants';
import { UserPreferenceName } from '@/modules/userPreferences';
import {
  FIELDS_PADDING_BOTTOM,
  FIELDS_SPACING,
  PANEL_ACCORDION_SX,
} from '@/modules/common/constants/propertyPanel';

interface Props {
  selectedShuttleRacks: Templates['shuttleRacks'];
}

function ShuttleTemplateParametersComponent({ selectedShuttleRacks }: Props) {
  const { t } = useTranslation();
  const mode = useAppStore((state) => state.mode);
  const loadingSvc = useMemo(() => LoadingServiceFactory.getInstance().createLoadingService(), []);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const tpsSvc = useMemo(() => TpsServiceFactory.getInstance().createTpsService(), []);

  // null means the selected shuttle racks have different values ('Mixed')
  const commonParams = useMemo(() => {
    const params = selectedShuttleRacks.map((shuttleRack) => shuttleRack.params);

    return {
      numColumns: getCommonNumber(params.map(({ numColumns }) => numColumns)),
      numLevels: getCommonNumber(params.map(({ numLevels }) => numLevels)),
      numDeep: getCommonNumber(params.map(({ numDeep }) => numDeep)),
      entrySide: getCommonOption(params.map(({ entrySide }) => entrySide)),
    };
  }, [selectedShuttleRacks]);

  /**
   * Regenerates every selected shuttle rack with the changed params, one request after the other,
   * as one undo step. Each rack keeps its own other params (position, angle, ...).
   */
  const regenerate = useCallback(
    async (params?: Partial<ShuttleRackParams>) => {
      if (selectedShuttleRacks.length === 0) return;

      loadingSvc.showLoadContainer('TRANSPARENT');

      try {
        const regeneratedIds = await tpsSvc.regenerateTemplates(
          selectedShuttleRacks.map((shuttleRack) => ({
            ...shuttleRack,
            params: { ...shuttleRack.params, ...params },
          })),
        );
        // Regenerating can give the racks new ids: keep all of them selected
        tpsSvc.selectTemplates(Array.from(regeneratedIds.values()));
      } finally {
        loadingSvc.hideLoadContainer();
      }
    },
    [loadingSvc, selectedShuttleRacks, tpsSvc],
  );

  const onNumColumnsChange = useCallback(
    (newValue: number) => {
      if (newValue < SHUTTLE_RACK.MIN.NUM_COLUMNS) {
        return {
          error: t('common:minimum_value', `Minimum ${SHUTTLE_RACK.MIN.NUM_COLUMNS}`, {
            minValue: SHUTTLE_RACK.MIN.NUM_COLUMNS,
          }),
        };
      }

      fddmSvc.setPreference('template.shuttle.numColumns', newValue);
      regenerate({ numColumns: newValue });
    },
    [fddmSvc, regenerate, t],
  );

  const onNumLevelsChange = useCallback(
    (newValue: number) => {
      if (newValue < SHUTTLE_RACK.MIN.NUM_LEVELS) {
        return {
          error: t('common:minimum_value', `Minimum ${SHUTTLE_RACK.MIN.NUM_LEVELS}`, {
            minValue: SHUTTLE_RACK.MIN.NUM_LEVELS,
          }),
        };
      }

      fddmSvc.setPreference('template.shuttle.numLevels', newValue);
      regenerate({ numLevels: newValue });
    },
    [fddmSvc, regenerate, t],
  );

  const onNumDeepChange = useCallback(
    (newValue: number) => {
      if (newValue < SHUTTLE_RACK.MIN.NUM_DEEP) {
        return {
          error: t('common:minimum_value', `Minimum ${SHUTTLE_RACK.MIN.NUM_DEEP}`, {
            minValue: SHUTTLE_RACK.MIN.NUM_DEEP,
          }),
        };
      }

      fddmSvc.setPreference('template.shuttle.numDeep', newValue);
      regenerate({ numDeep: newValue });
    },
    [fddmSvc, regenerate, t],
  );

  const onEntrySideChange = useCallback(
    (_: unknown, newValue: ShuttleRackParams['entrySide'] | null) => {
      if (!newValue || newValue === commonParams.entrySide) return;
      fddmSvc.setPreference('template.shuttle.entrySide', newValue);
      regenerate({ entrySide: newValue });
    },
    [fddmSvc, regenerate, commonParams.entrySide],
  );

  const isDisabled = mode !== 'EDITABLE';

  if (selectedShuttleRacks.length === 0) return null;

  return (
    <Accordion
      persistId={UserPreferenceName.ACCORDION_PROPERTY_PANEL_TEMPLATE_SHUTTLE_RACK}
      title={t('interface:properties.template_title.shuttle_rack', 'Shuttle Rack')}
      unmountOnExit
      sx={PANEL_ACCORDION_SX}
    >
      <Stack direction='column' spacing={FIELDS_SPACING} pb={FIELDS_PADDING_BOTTOM}>
        <PropertiesLabel i18nkey='interface:properties.template.columns'>
          <NumberInput
            value={commonParams.numColumns}
            onChange={onNumColumnsChange}
            minValue={1}
            disabled={isDisabled}
          />
        </PropertiesLabel>
        <PropertiesLabel i18nkey='interface:properties.template.levels'>
          <NumberInput
            value={commonParams.numLevels}
            onChange={onNumLevelsChange}
            disabled={isDisabled}
            minValue={2}
          />
        </PropertiesLabel>
        <PropertiesLabel i18nkey='interface:properties.template.units_deep'>
          <NumberInput
            value={commonParams.numDeep}
            onChange={onNumDeepChange}
            minValue={1}
            disabled={isDisabled}
          />
        </PropertiesLabel>
        <PropertiesLabel i18nkey='interface:properties.template.entry_side.label'>
          <ToggleButtonGroup
            value={commonParams.entrySide}
            exclusive
            onChange={onEntrySideChange}
            disabled={isDisabled}
          >
            <ToggleButton
              value='FRONT'
              aria-label='Enable shuttle rack entry from a single side'
              sx={{ p: 0, fontSize: '13px' }}
            >
              {t('interface:properties.template.entry_side.single')}
            </ToggleButton>
            <ToggleButton
              value='FRONT_AND_BACK'
              aria-label='Enable shuttle rack entry from both sides'
              sx={{ p: 0, fontSize: '13px' }}
            >
              {t('interface:properties.template.entry_side.both')}
            </ToggleButton>
          </ToggleButtonGroup>
        </PropertiesLabel>
      </Stack>
    </Accordion>
  );
}

export const ShuttleTemplateParameters = memo(ShuttleTemplateParametersComponent);
